import { useContext, useEffect, useRef, useState } from 'react';
import NotificationsContext from '../../contexts/data/NotificationsContext';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { Table } from 'antd';
import { CONTACTS_PAGE_PATH } from '../../pages/contacts/ContactsPage';
import AugmentedNotifications from '../../../domain/models/@aggregates/AugmentedNotifications';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { InboxOutlined } from '@ant-design/icons';
import { advancedOnRow } from '../../../core/utils/commonUtils';

interface NotificationsListProps {
  scopedSubscriptionId: string | null;
  pendingOnly: boolean | false;
}

const NotificationsList: React.FC<NotificationsListProps> = ({
  scopedSubscriptionId,
  pendingOnly,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState(
    [] as AugmentedNotifications[]
  );
  const notificationsContext = useContext(NotificationsContext);
  const windowSize = useWindowSize();
  const fetchNextCalled = useRef(false);
  const navigate = useNavigate();

  const handlePaginationChange = (page: number) => {
    setCurrentPage(page);
    const currentSlice = notificationsContext.items.slice(
      (page - 1) * notificationsContext.hitsPerPage,
      page * notificationsContext.hitsPerPage
    );
    if (currentSlice.length === 0) {
      fetchNextCalled.current = true;
      notificationsContext.fetchNext();
    }
  };

  const handlePageSizeChange = (_current: number, newPageSize: number) => {
    setCurrentPage(1);
    notificationsContext.fetch(scopedSubscriptionId, pendingOnly, newPageSize);
  };

  // This hook is used to initialize the notifications list
  useEffect(() => {
    if (
      notificationsContext.status === ContextStatus.IDLE ||
      notificationsContext.scopedSubscriptionId !== scopedSubscriptionId
    ) {
      notificationsContext.fetch(scopedSubscriptionId, pendingOnly);
    }
    return () => {
      notificationsContext.reset();
    };
  }, []);

  // This hook is triggered when a 'page next' is called to get the new slice of items
  useEffect(() => {
    if (notificationsContext.status === ContextStatus.SUCCESS) {
      const currentSlice = notificationsContext.items.slice(
        (currentPage - 1) * notificationsContext.hitsPerPage,
        currentPage * notificationsContext.hitsPerPage
      );
      // Synchro avec un contexte qui fetche : l'etat local suit le resultat d'un
      // chargement declenche ici. Architecture Context + use case du repo.
      // eslint-disable-next-line react/set-state-in-effect
      setCurrentData(currentSlice);
      fetchNextCalled.current = false;
    }
  }, [notificationsContext.status]);

  // This hook is triggered when the current page changes to update the displayed data
  useEffect(() => {
    if (!fetchNextCalled.current) {
      const currentSlice = notificationsContext.items.slice(
        (currentPage - 1) * notificationsContext.hitsPerPage,
        currentPage * notificationsContext.hitsPerPage
      );
      setCurrentData(currentSlice);
    }
  }, [currentPage]);

  // This hook is used to go back to first page when refreshing the list
  // Retour a la page 1 quand la tranche courante est vide. Calcule pendant le
  // rendu (pattern « ajuster l'etat quand une prop change » de la doc React) :
  // dans un effet, chaque fin de chargement coutait un second rendu du tableau.
  const [lastStatusForClamp, setLastStatusForClamp] = useState(notificationsContext.status);
  if (notificationsContext.status !== lastStatusForClamp) {
    setLastStatusForClamp(notificationsContext.status);
    if (
      notificationsContext.status === ContextStatus.SUCCESS &&
      notificationsContext.items.slice(
        (currentPage - 1) * notificationsContext.hitsPerPage,
        currentPage * notificationsContext.hitsPerPage
      ).length === 0
    ) {
      setCurrentPage(1);
    }
  }

  return (
    <Table
      pagination={{
        current: currentPage,
        total: notificationsContext.count,
        pageSize: notificationsContext.hitsPerPage,
        onChange: handlePaginationChange,
        onShowSizeChange: (_current, newSize) => handlePageSizeChange(_current, newSize),
        showSizeChanger: true,
        simple: true,
        pageSizeOptions: [20, 50, 100, 200],
        position: ['bottomRight', 'topRight'],
        disabled: notificationsContext.status === ContextStatus.LOADING,
      }}
      loading={notificationsContext.status === ContextStatus.LOADING}
      columns={
        windowSize.width > 576
          ? [
            {
              title: 'Nom complet',
              dataIndex: 'contactFullname',
              key: 'contactFullname',
            },
            {
              title: 'Date',
              render: (record: AugmentedNotifications) => {
                return (
                  <div>
                    <InboxOutlined style={{ marginRight: 6 }} /> Notification envoyée par email{' '}
                    {moment(record?.notifications?.updated).fromNow()}
                  </div>
                );
              },
            },
          ]
          : [
            {
              title: 'Nom complet',
              dataIndex: 'contactFullname',
              key: 'contactFullname',
            },
            {
              title: 'Date',
              render: (record: AugmentedNotifications) => {
                return (
                  <div>
                    <InboxOutlined style={{ marginRight: 6 }} /> Email envoyé{' '}
                    {moment(record?.notifications?.updated).fromNow()}
                  </div>
                );
              },
            },
          ]
      }
      rowKey={(record) => record.id}
      dataSource={currentData}
      onRow={(record) =>
        advancedOnRow({
          url: `${CONTACTS_PAGE_PATH}/${record.contact.id}`,
          navigate,
          className: 'table-row',
        })
      }
    />
  );
};

export default NotificationsList;
